const normalizeId = (value) => (value == null ? null : String(value));

const mergeProduct = (current, patch) => {
  if (!patch) return current;
  const next = { ...current, ...patch };
  if (!Object.prototype.hasOwnProperty.call(patch, 'Varieties')) {
    next.Varieties = current?.Varieties || [];
  }
  return next;
};

const mergeVariety = (current, patch) => {
  if (!patch) return current;
  const next = { ...current, ...patch };
  if (!Object.prototype.hasOwnProperty.call(patch, 'VarietyPrices')) {
    next.VarietyPrices = current?.VarietyPrices || [];
  }
  return next;
};

const findVarietyLocation = (products, varietyId) => {
  if (!varietyId) return null;
  const target = normalizeId(varietyId);
  for (let i = 0; i < products.length; i += 1) {
    const varieties = products[i]?.Varieties || [];
    for (let j = 0; j < varieties.length; j += 1) {
      if (normalizeId(varieties[j]?.id) === target) {
        return { productIndex: i, varietyIndex: j };
      }
    }
  }
  return null;
};

const findPriceLocation = (products, priceId) => {
  if (!priceId) return null;
  const target = normalizeId(priceId);
  for (let i = 0; i < products.length; i += 1) {
    const varieties = products[i]?.Varieties || [];
    for (let j = 0; j < varieties.length; j += 1) {
      const prices = varieties[j]?.VarietyPrices || [];
      for (let k = 0; k < prices.length; k += 1) {
        if (normalizeId(prices[k]?.id) === target) {
          return { productIndex: i, varietyIndex: j, priceIndex: k };
        }
      }
    }
  }
  return null;
};

const updateProductAt = (products, index, patch) => {
  const next = [...products];
  const current = products[index];
  next[index] = mergeProduct(current, patch);
  return next;
};

const updateVarietyAt = (products, productIndex, varietyIndex, patch) => {
  const product = products[productIndex];
  const nextVarieties = [...(product?.Varieties || [])];
  nextVarieties[varietyIndex] = mergeVariety(nextVarieties[varietyIndex], patch);
  const nextProduct = { ...product, Varieties: nextVarieties };
  const nextProducts = [...products];
  nextProducts[productIndex] = nextProduct;
  return nextProducts;
};

const updatePriceAt = (products, productIndex, varietyIndex, priceIndex, patch) => {
  const product = products[productIndex];
  const varieties = [...(product?.Varieties || [])];
  const variety = varieties[varietyIndex];
  const prices = [...(variety?.VarietyPrices || [])];
  prices[priceIndex] = { ...prices[priceIndex], ...patch };
  varieties[varietyIndex] = { ...variety, VarietyPrices: prices };
  const nextProduct = { ...product, Varieties: varieties };
  const nextProducts = [...products];
  nextProducts[productIndex] = nextProduct;
  return nextProducts;
};

export const applyProductEvent = (products, event) => {
  const list = Array.isArray(products) ? products : [];
  if (!event) return null;
  const action = event.action || event.type;

  switch (action) {
    case 'create-product': {
      const product = event.product || event.productPatch;
      if (!product) return null;
      const targetId = normalizeId(product.id || event.productId);
      if (!targetId) return null;
      const existingIndex = list.findIndex((item) => normalizeId(item?.id) === targetId);
      if (existingIndex !== -1) {
        return updateProductAt(list, existingIndex, product);
      }
      return [...list, product];
    }
    case 'update-product': {
      const product = event.product || event.productPatch;
      const targetId = normalizeId(product?.id || event.productId);
      if (!targetId) return null;
      const index = list.findIndex((item) => normalizeId(item?.id) === targetId);
      if (index === -1) {
        if (!product) return null;
        return [...list, { ...product, id: product?.id ?? event.productId }];
      }
      return updateProductAt(list, index, product || { id: event.productId });
    }
    case 'delete-product': {
      const targetId = normalizeId(event.productId);
      if (!targetId) return null;
      const next = list.filter((item) => normalizeId(item?.id) !== targetId);
      return next.length === list.length ? list : next;
    }
    case 'add-variety': {
      const variety = event.variety || event.varietyPatch;
      const productId = event.productId || variety?.productId || variety?.ProductId;
      if (!variety || !productId) return null;
      const productIndex = list.findIndex((item) => normalizeId(item?.id) === normalizeId(productId));
      if (productIndex === -1) return null;
      const product = list[productIndex];
      const varieties = [...(product?.Varieties || [])];
      const exists = varieties.some((item) => normalizeId(item?.id) === normalizeId(variety.id));
      if (!exists) {
        varieties.push(variety);
      }
      const nextProduct = { ...product, Varieties: varieties };
      const nextProducts = [...list];
      nextProducts[productIndex] = nextProduct;
      return nextProducts;
    }
    case 'update-variety':
    case 'variety-sold-out':
    case 'variety-available': {
      const variety = event.variety || event.varietyPatch;
      const targetId = normalizeId(variety?.id || event.varietyId);
      if (!targetId) return null;
      const location = findVarietyLocation(list, targetId);
      if (!location) return null;
      return updateVarietyAt(
        list,
        location.productIndex,
        location.varietyIndex,
        variety || { id: event.varietyId, isSoldOut: event.isSoldOut }
      );
    }
    case 'delete-variety': {
      const targetId = normalizeId(event.varietyId);
      if (!targetId) return null;
      const location = findVarietyLocation(list, targetId);
      if (!location) return list;
      const product = list[location.productIndex];
      const varieties = (product?.Varieties || []).filter(
        (item) => normalizeId(item?.id) !== targetId
      );
      const nextProduct = { ...product, Varieties: varieties };
      const nextProducts = [...list];
      nextProducts[location.productIndex] = nextProduct;
      return nextProducts;
    }
    case 'add-variety-price': {
      const price = event.price || event.pricePatch;
      const varietyId = event.varietyId || price?.varietyId || price?.VarietyId;
      if (!price || !varietyId) return null;
      const location = findVarietyLocation(list, varietyId);
      if (!location) return null;
      const product = list[location.productIndex];
      const varieties = [...(product?.Varieties || [])];
      const variety = varieties[location.varietyIndex];
      const prices = [...(variety?.VarietyPrices || [])];
      const exists = prices.some((item) => normalizeId(item?.id) === normalizeId(price.id));
      if (!exists) {
        prices.push(price);
      }
      varieties[location.varietyIndex] = { ...variety, VarietyPrices: prices };
      const nextProduct = { ...product, Varieties: varieties };
      const nextProducts = [...list];
      nextProducts[location.productIndex] = nextProduct;
      return nextProducts;
    }
    case 'update-variety-price':
    case 'price-sold-out':
    case 'price-available': {
      const price = event.price || event.pricePatch;
      const targetId = normalizeId(price?.id || event.priceId);
      if (!targetId) return null;
      let location = findPriceLocation(list, targetId);
      if (!location && (event.varietyId || price?.varietyId)) {
        const varietyLocation = findVarietyLocation(list, event.varietyId || price?.varietyId);
        if (varietyLocation) {
          const product = list[varietyLocation.productIndex];
          const variety = product?.Varieties?.[varietyLocation.varietyIndex];
          const prices = variety?.VarietyPrices || [];
          const priceIndex = prices.findIndex((item) => normalizeId(item?.id) === targetId);
          if (priceIndex !== -1) {
            location = { ...varietyLocation, priceIndex };
          }
        }
      }
      if (!location) return null;
      return updatePriceAt(
        list,
        location.productIndex,
        location.varietyIndex,
        location.priceIndex,
        price || { id: event.priceId, isSoldOut: event.isSoldOut }
      );
    }
    case 'delete-variety-price': {
      const targetId = normalizeId(event.priceId);
      if (!targetId) return null;
      const location = findPriceLocation(list, targetId);
      if (!location) return list;
      const product = list[location.productIndex];
      const varieties = [...(product?.Varieties || [])];
      const variety = varieties[location.varietyIndex];
      const prices = (variety?.VarietyPrices || []).filter(
        (item) => normalizeId(item?.id) !== targetId
      );
      varieties[location.varietyIndex] = { ...variety, VarietyPrices: prices };
      const nextProduct = { ...product, Varieties: varieties };
      const nextProducts = [...list];
      nextProducts[location.productIndex] = nextProduct;
      return nextProducts;
    }
    default:
      return null;
  }
};
